import styled from '@emotion/styled';
import moment from 'moment-timezone';
import { FC, useState } from 'react';

import { useUserSettings } from '../../context/UserSettingsContext';
import { Calculation } from '../../../types/tecton_proto/data/feature_view';
import DataflowContainer from '@tecton/ComponentRedesign/Dataflow/DataflowContainer';
import {
  AnyFCO,
  DataSourceFCO,
  FCOFields,
  FCOType,
  FeatureViewFCO,
  FeatureViewFCOFields,
  TemporalAggregateFeatureForFCO,
  TransformationFCO,
  TransformationFCOFields,
  WorkspaceFCOContainer,
} from '../../../core/types/fcoTypes';

import {
  Button,
  Card,
  Code,
  ColumnLayout,
  ExtendedTableColumn,
  FlexGroup,
  FlexItem,
  Flyout,
  Icon,
  Link,
  Table,
  Tooltip,
} from '@lithos';

import { dataflowSpecSelector, idFormatter } from '@tecton/ComponentRedesign/Dataflow/workspaceDataflowSpecSelector';
import TectonCode from '@tecton/ComponentRedesign/lib/Code';
import { dateTimeFormat, timeZone } from '@lithos/DateUtils';

interface CalculationWithId extends Calculation {
  id: string;
}

interface FeatureViewDataflowContainerProps {
  fco: FeatureViewFCO;
  workspaceData: WorkspaceFCOContainer;
  idToFcoMap: Record<string, AnyFCO>;
}

const DataflowContainerWrapper = styled.div`
  margin-bottom: ${({ theme }) => theme.padding.l};
  height: 50vh;
`;

const WINDOW_AGGREGATION = 'Window Aggregations';

const FeatureViewDataflowContainer: FC<FeatureViewDataflowContainerProps> = ({ fco, workspaceData, idToFcoMap }) => {
  const { workspace } = useUserSettings();
  const [openFlyoutId, setIsFlyoutOpen] = useState<string | null | undefined>();

  const data = dataflowSpecSelector(workspaceData, fco.name ?? '', {
    id: idFormatter(fco),
    type: FCOType.FEATURE_VIEW,
  });

  const sources = fco[FeatureViewFCOFields.DATA_SOURCE_IDS].map((id) => idToFcoMap[id] as DataSourceFCO);
  const transformations = fco[FeatureViewFCOFields.ALL_TRANSFORMATIONS].map(
    (id) => idToFcoMap[id] as TransformationFCO
  );

  if (fco[FeatureViewFCOFields.TEMPORAL_AGGREGATE_FEATURES].length > 0) {
    transformations.unshift({
      name: WINDOW_AGGREGATION,
      description: '',
      createdDate: moment(fco[FCOFields.CREATED_DATE]).toDate(),
      owner: fco.owner,
      code:
        fco.temporalAggregateFeatures
          ?.map((i: TemporalAggregateFeatureForFCO) => {
            return `${i.aggregation} over ${i.aggregationWindow} as ${i.name}`;
          })
          .join('\n') ?? '',
      [TransformationFCOFields.MODE]: undefined,
      [TransformationFCOFields.DEPENDENT_FEATURE_VIEWS]: [],
      [TransformationFCOFields.DEPENDENT_FEATURE_SERVICES]: [],
      [FCOFields.ID]: fco[FCOFields.ID],
      [FCOFields.TRUNCATED_DESCRIPTION]: '',
      [FCOFields.TAGS]: [],
      [FCOFields.FCO_TYPE]: FCOType.DATA_SOURCE,
      [FCOFields.LAST_MODIFIED_BY]: undefined,
      [FCOFields.LAST_MODIFIED_DATE]: undefined,
      [FCOFields.WORKSPACE]: undefined,
      [FCOFields.SOURCE_FILE_NAME]: undefined,
      [FCOFields.PREVENT_DESTROY]: undefined,
    });
  }

  const sourceColumns: ExtendedTableColumn<DataSourceFCO>[] = [
    {
      name: 'Source',
      content: (source: DataSourceFCO) => (
        <Tooltip
          content={source.description}
          trigger={
            <Link url={`/repo/${workspace}/data-sources/${source.name}/overview`} icon="Sources" name={source.name} />
          }
        />
      ),
    },
    {
      name: 'Created',
      content: (source: DataSourceFCO) =>
        source[FCOFields.CREATED_DATE] ? dateTimeFormat(source[FCOFields.CREATED_DATE], timeZone.UTC) : <>n/a</>,
    },
  ];

  const calculationColumns = [
    {
      name: 'Name',
      content: (calc: CalculationWithId) => <>{calc.name}</>,
    },
    {
      name: 'Expression',
      content: (calc: CalculationWithId) => (
        <>
          <Button
            onClick={() => {
              setIsFlyoutOpen(calc?.id);
            }}
            icon="CodeDocument"
            type="ghost"
            label="View Expression"
          />
          {openFlyoutId === calc?.id && (
            <Flyout
              title={calc?.name}
              isOpen={openFlyoutId !== null}
              setIsOpen={() => {
                setIsFlyoutOpen(null);
              }}
              content={<Code code={calc?.expr ?? ''} />}
            />
          )}
        </>
      ),
    },
  ];

  const transformationColumns: ExtendedTableColumn<TransformationFCO>[] = [
    {
      name: 'Source',
      content: (transformation: TransformationFCO) => {
        if (transformation[FCOFields.NAME] === WINDOW_AGGREGATION) {
          return (
            <Tooltip
              title={`${transformation.name}`}
              content={''}
              data-testid="window-aggregation-tooltip-content"
              trigger={
                <>
                  <FlexGroup gapSize="2" direction={'row'} justifyContent="center" alignItems="center">
                    <FlexItem grow={0}>{<Icon icon="Aggregation" type="mono" size="s" />} </FlexItem>
                    <FlexItem> {WINDOW_AGGREGATION} </FlexItem>
                  </FlexGroup>
                </>
              }
            />
          );
        }

        return (
          <Tooltip
            content={transformation.description}
            trigger={
              <Link
                url={`/repo/${workspace}/transformations/${transformation.name}/overview`}
                icon="Transformations"
                name={transformation.name}
              />
            }
          />
        );
      },
    },
    {
      name: 'Definition',
      content: (transformation: TransformationFCO) => (
        <>
          <Button
            onClick={() => {
              setIsFlyoutOpen(transformation?.id);
            }}
            icon="CodeDocument"
            type="ghost"
            label="View Definition"
          />
          {openFlyoutId === transformation?.id && (
            <Flyout
              title={transformation[FCOFields.NAME]}
              isOpen={openFlyoutId !== null}
              setIsOpen={() => {
                setIsFlyoutOpen(null);
              }}
              content={<TectonCode code={transformation[TransformationFCOFields.CODE] ?? ''} />}
            />
          )}
        </>
      ),
    },
  ];

  const calculationsWithIds = fco?.calculations?.map((calc, ind) => ({
    ...calc,
    id: fco?.id + calc.name + ind,
  }));

  return (
    <>
      <DataflowContainerWrapper>
        <DataflowContainer isFcoLevelDiagram={true} data={data} />
      </DataflowContainerWrapper>
      <ColumnLayout>
        <Card title="Sources">
          <Table items={sources} columns={sourceColumns} />
        </Card>
        {fco?.isCalculation ? (
          <Card title="Calculations">
            <Table items={calculationsWithIds || []} columns={calculationColumns} />
          </Card>
        ) : (
          <Card title="Transformations">
            <Table items={transformations} columns={transformationColumns} />
          </Card>
        )}
      </ColumnLayout>
    </>
  );
};

export default FeatureViewDataflowContainer;
